'use client';

import { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { Treatment } from '@/lib/treatments-data';
import OptimizedImage from '@/components/optimized-media/OptimizedImage';
import { Button } from '@/components/ui/button';
import { ArrowRight, Star, Shield, Award, CheckCircle, Sparkles } from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';
import { useRTL } from '@/hooks/useRTL';
import { cn } from '@/lib/utils';

interface HeroSectionProps {
    treatment: Treatment;
}

export default function HeroSection({ treatment }: HeroSectionProps) {
    const { t, isLoading } = useTranslation('teeth-whitening');
    const { isRTL } = useRTL();
    const sectionRef = useRef<HTMLElement>(null);
    const titleRef = useRef<HTMLHeadingElement>(null);
    const subtitleRef = useRef<HTMLParagraphElement>(null);
    const ctaRef = useRef<HTMLDivElement>(null);
    const imageRef = useRef<HTMLDivElement>(null);
    const trustBadgesRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (isLoading) return;

        const ctx = gsap.context(() => {
            // Hero title animation - fade up + slide in
            gsap.fromTo(titleRef.current,
                { opacity: 0, y: 60, scale: 0.95 },
                {
                    opacity: 1,
                    y: 0,
                    scale: 1,
                    duration: 1.2,
                    ease: "power3.out",
                    delay: 0.3
                }
            );

            // Subtitle animation
            gsap.fromTo(subtitleRef.current,
                { opacity: 0, y: 40 },
                {
                    opacity: 1,
                    y: 0,
                    duration: 1,
                    delay: 0.6,
                    ease: "power3.out"
                }
            );

            // CTA animation
            gsap.fromTo(ctaRef.current,
                { opacity: 0, y: 30 },
                {
                    opacity: 1,
                    y: 0,
                    duration: 0.8,
                    delay: 0.9,
                    ease: "power3.out"
                }
            );

            // Trust badges animation
            gsap.fromTo(trustBadgesRef.current,
                { opacity: 0, y: 20 },
                {
                    opacity: 1,
                    y: 0,
                    duration: 0.8,
                    delay: 1.1,
                    ease: "power3.out"
                }
            );

            // Floating image animation
            gsap.fromTo(imageRef.current,
                { opacity: 0, scale: 0.8, rotation: -10 },
                {
                    opacity: 1,
                    scale: 1,
                    rotation: 0,
                    duration: 1.5,
                    delay: 0.4,
                    ease: "elastic.out(1, 0.5)"
                }
            );

            // Gentle floating animation for the image
            gsap.to(imageRef.current, {
                y: -10,
                duration: 3,
                repeat: -1,
                yoyo: true,
                ease: "power2.inOut"
            });

        }, sectionRef);

        return () => ctx.revert();
    }, [isLoading]);

    const handleConsultationClick = () => {
        const candidatesSection = document.getElementById('candidates');
        candidatesSection?.scrollIntoView({ behavior: 'smooth' });
    };

    if (isLoading) {
        return (
            <section className="relative min-h-[65vh] bg-gradient-to-br from-navy to-navy-950 overflow-hidden">
                <div className="container relative py-10 lg:py-16">
                    <div className="grid lg:grid-cols-2 gap-12 items-center min-h-[52vh]">
                        <div className="space-y-8 animate-pulse">
                            <div className="h-16 bg-gray-200 dark:bg-gray-700 rounded"></div>
                            <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded"></div>
                            <div className="h-12 bg-gray-200 dark:bg-gray-700 rounded w-48"></div>
                            <div className="space-y-4">
                                <div className="grid grid-cols-3 gap-4">
                                    {[1, 2, 3].map((i) => (
                                        <div key={i} className="h-12 bg-gray-200 dark:bg-gray-700 rounded"></div>
                                    ))}
                                </div>
                            </div>
                        </div>
                        <div className="relative flex justify-center lg:justify-end">
                            <div className="w-80 h-80 md:w-96 md:h-96 bg-gray-200 dark:bg-gray-700 rounded-full animate-pulse"></div>
                        </div>
                    </div>
                </div>
            </section>
        );
    }

    return (
        <section
            id="hero"
            ref={sectionRef}
            className="relative min-h-[65vh] bg-gradient-to-br from-navy to-navy-950 overflow-hidden"
        >
            {/* Background decorative elements */}
            <div className="absolute inset-0">
                <div className="absolute top-1/4 right-0 w-96 h-96 bg-gold opacity-10 rounded-full blur-3xl"></div>
                <div className="absolute bottom-1/4 left-0 w-80 h-80 bg-gold opacity-10 rounded-full blur-2xl"></div>
            </div>

            <div className="container relative py-10 lg:py-16">
                <div className="grid lg:grid-cols-2 gap-12 items-center min-h-[52vh]">
                    {/* Left Content */}
                    <div className={`space-y-8 text-center ${isRTL ? 'lg:text-right' : 'lg:text-left'}`}>
                        <h1
                            ref={titleRef}
                            className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight font-serif"
                        >
                            {t('hero.title.part1')}{' '}
                            <span className="text-gold">{t('hero.title.part2')}</span>
                        </h1>

                        <p
                            ref={subtitleRef}
                            className="text-xl md:text-2xl text-slate-300 leading-relaxed max-w-2xl mx-auto lg:mx-0"
                        >
                            {t('hero.subtitle')}
                        </p>

                        <div ref={ctaRef} className="space-y-6">
                            <Button
                                onClick={handleConsultationClick}
                                size="lg" 
                                className={`bg-gold hover:bg-white hover:text-navy hover:border-navy text-navy px-8 py-6 text-lg font-semibold rounded-xl shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-105 border-2 border-gold flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}
                            >
                                <Sparkles className={`w-5 h-5 ${isRTL ? 'rotate-180' : ''}`} />
                                <span>{t('hero.cta.button')}</span>
                            </Button>

                            {/* Trust indicators — centered flex-wrap so mobile
                                shows badges side-by-side instead of stacked.
                                RTL reverses order + aligns end on desktop. */}
                            <div ref={trustBadgesRef} className="space-y-3">
                                <div className={cn(
                                    "flex flex-wrap justify-center gap-2 text-sm",
                                    isRTL ? "lg:justify-end lg:flex-row-reverse" : "lg:justify-start"
                                )}>
                                    <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-lg px-3 py-2">
                                        <span className="text-white font-medium">{t('hero.trustBadges.certifications.fda')}</span>
                                        <CheckCircle className="w-4 h-4 text-gold" />
                                    </div>
                                    <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-lg px-3 py-2">
                                        <span className="text-white font-medium">{t('hero.trustBadges.certifications.ada')}</span>
                                        <Shield className="w-4 h-4 text-gold" />
                                    </div>
                                    <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-lg px-3 py-2">
                                        <span className="text-white font-medium">{t('hero.trustBadges.certifications.iso')}</span>
                                        <Award className="w-4 h-4 text-gold" />
                                    </div>
                                </div>

                                <div className={cn(
                                    "flex flex-wrap justify-center gap-x-5 gap-y-2 text-sm text-slate-300",
                                    isRTL ? "lg:justify-end lg:flex-row-reverse" : "lg:justify-start"
                                )}>
                                    <div className="flex items-center gap-2">
                                        <span className="whitespace-nowrap">{t('hero.trustBadges.highlights.instant_results')}</span>
                                        <Sparkles className="w-4 h-4 text-gold fill-current" />
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <span className="whitespace-nowrap">{t('hero.trustBadges.highlights.safe_procedure')}</span>
                                        <Shield className="w-4 h-4 text-gold" />
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <span className="whitespace-nowrap">{t('hero.trustBadges.highlights.professional_grade')}</span>
                                        <Award className="w-4 h-4 text-gold" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Right Image */}
                    <div className="relative flex justify-center lg:justify-end">
                        <div ref={imageRef} className="relative z-10">
                            <div className="w-80 h-80 md:w-96 md:h-96 relative">
                                <OptimizedImage
                                    src={treatment.heroImage}
                                    alt="Professional Teeth Whitening"
                                    width={400}
                                    height={400}
                                    priority
                                    className="rounded-full shadow-2xl border-4 border-gold/20"
                                />
                                {/* Floating dental elements — smaller + tucked
                                    in on mobile so they don't overflow. */}
                                <div className="absolute -top-2 -right-2 sm:-top-4 sm:-right-4 w-12 h-12 sm:w-16 sm:h-16 bg-gold rounded-full flex items-center justify-center shadow-lg">
                                    <Sparkles className="w-6 h-6 sm:w-8 sm:h-8 text-navy" />
                                </div>
                                <div className="absolute -bottom-2 -left-2 sm:-bottom-4 sm:-left-4 w-10 h-10 sm:w-12 sm:h-12 bg-white rounded-full flex items-center justify-center shadow-lg">
                                    <span className="text-lg sm:text-xl">😁</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Scroll indicator */}
                <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 text-slate-300 animate-bounce">
                    <div className="flex flex-col items-center space-y-2">
                        <span className="text-sm">{t('hero.cta.scrollText')}</span>
                        <div className="w-px h-8 bg-gold"></div>
                    </div>
                </div>
            </div>
        </section>
    );
}